import { Model } from "@lumora/core";
import { SITE_CONFIG, type SiteConfig } from "../siteConfig.js";

export interface PageSeoInit {
  /** Hash route, e.g. "/packages". "/" is home. */
  route: string;
  title: string;
  description: string;
  /** Excluded from sitemap and marked noindex (e.g. unknown-route fallbacks). */
  noindex?: boolean;
}

/** SEO metadata for one page; builds URLs, OG tags and JSON-LD from SITE_CONFIG. */
export class PageSeo extends Model {
  readonly route: string;
  readonly title: string;
  readonly description: string;
  readonly noindex: boolean;

  constructor(init: PageSeoInit, private readonly cfg: SiteConfig = SITE_CONFIG) {
    super();
    this.route = init.route;
    this.title = init.title;
    this.description = init.description;
    this.noindex = init.noindex ?? false;
  }

  validate(): string[] {
    const errors: string[] = [];
    if (this.title.length > 70) errors.push("title too long");
    if (this.description.length > 170) errors.push("description too long");
    return errors;
  }

  /** Public URL. The site uses hash routing, so non-home routes live after "#". */
  url(): string {
    return this.route === "/" ? `${this.cfg.origin}/` : `${this.cfg.origin}/#${this.route}`;
  }

  imageUrl(): string {
    return `${this.cfg.origin}${this.cfg.ogImage}`;
  }

  /** schema.org Service. No address or phone: Turnstill is an online studio. */
  jsonLd(): Record<string, unknown> {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: this.route === "/" ? "3D product renders and spins" : this.title,
      serviceType: "3D product rendering",
      description: this.description,
      url: this.url(),
      image: this.imageUrl(),
      areaServed: "Worldwide",
      provider: { "@type": "Organization", name: this.cfg.name, url: `${this.cfg.origin}/` },
    };
  }

  /** Tags for <head>, as data, so both the runtime hook and the build can use them. */
  metaTags(): { name?: string; property?: string; content: string }[] {
    return [
      { name: "description", content: this.description },
      { name: "robots", content: this.noindex ? "noindex,follow" : "index,follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: this.cfg.name },
      { property: "og:title", content: this.title },
      { property: "og:description", content: this.description },
      { property: "og:url", content: this.url() },
      { property: "og:image", content: this.imageUrl() },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: this.title },
      { name: "twitter:description", content: this.description },
      { name: "twitter:image", content: this.imageUrl() },
    ];
  }
}
